import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, TextField, Button, CircularProgress, Alert,
  FormControlLabel, Switch, Grid, MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserForm = ({ viewerOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [subadmins, setSubadmins] = useState([]);
  const [loadingSubadmins, setLoadingSubadmins] = useState(false);

  // Determine if in viewer-only mode based on path or prop
  const isViewerMode = viewerOnly || location.pathname.startsWith('/viewers');
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const validationSchema = Yup.object({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    full_name: Yup.string().nullable(),
    phone: Yup.string().nullable(),
    role: Yup.string().required('Role is required'),
    is_active: Yup.boolean(),
    is_association_admin: Yup.boolean(),
    distributor_name: Yup.string().nullable().when('role', {
      is: 'subadmin',
      then: () => Yup.string().required('Distributor Name is required for subadmins')
    }),
    city: Yup.string().nullable().when('role', {
      is: 'subadmin',
      then: () => Yup.string().required('City is required for subadmins')
    }),
    association_id: isSuperAdmin
      ? Yup.number().nullable().when('role', {
          is: (role) => role !== 'superadmin',
          then: () => Yup.number().required('Association is required for non-SuperAdmin users')
        })
      : Yup.number().nullable(),
    created_by_id: Yup.number().nullable().when('role', {
      is: 'viewer',
      then: () => Yup.number().required('Linked SubAdmin is required for viewers')
    }),
    password: id
      ? Yup.string() // Optional for existing users
      : Yup.string().required('Password is required for new users')
  });

  // Get default role based on user type and context
  let defaultRole;
  if (isViewerMode) {
    defaultRole = 'viewer';
  } else if (isSuperAdmin) {
    defaultRole = 'subadmin'; // SuperAdmins default to creating subadmins
  } else if (currentUser?.role === 'subadmin' && currentUser?.is_association_admin) {
    defaultRole = 'subadmin'; // Association Admins default to creating subadmins
  } else {
    defaultRole = 'viewer'; // Regular SubAdmins default to creating viewers
  }
  const isAssociationAdmin = currentUser?.role === 'subadmin' && currentUser?.is_association_admin;

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      full_name: '',
      phone: '',
      role: defaultRole,
      is_active: true,
      is_association_admin: false,
      distributor_name: '',
      city: '',
      association_id: isSuperAdmin ? null : currentUser?.association_id,
      created_by_id: isViewerMode ? currentUser?.id : null,
      password: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        // Only include password in request if it's provided
        const userData = { ...values };
        if (!userData.password) delete userData.password;

        // Non-superadmin can only create users in their own association
        if (!isSuperAdmin) {
          userData.association_id = currentUser?.association_id;
        }
        
        // Special case: Check if we're trying to activate a viewer when one is already active
        if (currentUser?.role === 'subadmin' && 
            !currentUser?.is_association_admin && 
            userData.role === 'viewer' && 
            userData.is_active === true) {
          
          try {
            const existingViewersResponse = await api.get('/api/admin/users', {
              params: {
                role: 'viewer',
                created_by_id: currentUser.id
              }
            });
            
            console.log("Existing viewers:", existingViewersResponse.data);
            
            // We're only concerned about OTHER active viewers (excluding this one if editing)
            const otherActiveViewers = existingViewersResponse.data.filter(
              viewer => viewer.is_active && (!id || viewer.id.toString() !== id.toString())
            );
            console.log("Other active viewers:", otherActiveViewers);
            
            if (otherActiveViewers.length > 0) {
              setError('You can only have one active viewer at a time');
              toast.error('You can only have one active viewer at a time');
              return;
            } else {
              console.log("No other active viewers found, proceeding");
            }
          } catch (checkError) {
            console.error('Error checking existing viewers:', checkError);
          }
        }
        
        if (id) {
          // Update existing user
          try {
            await api.put(`/api/admin/users/${id}`, userData);
            toast.success('User updated successfully');
          } catch (updateError) {
            console.error('Error updating user:', updateError);
            setError(updateError.response?.data?.error || 'Failed to update user');
            toast.error(updateError.response?.data?.error || 'Failed to update user');
            throw updateError; // Rethrow to prevent navigation
          }
        } else {
          // Create new user
          await api.post('/api/admin/users', userData);
          toast.success('User created successfully');
        }

        // Navigate back to appropriate list
        if (isViewerMode) {
          navigate('/viewers');
        } else {
          navigate('/admin/users');
        }
      } catch (error) {
        console.error('Error saving user:', error);
        setError(error.response?.data?.error || 'Failed to save user');
        toast.error(error.response?.data?.error || 'Failed to save user');
      } finally {
        setLoading(false);
      }
    }
  });

  // Fetch associations for dropdown (only for superadmin)
  useEffect(() => {
    const fetchAssociations = async () => {
      if (!isSuperAdmin) return;

      try {
        setLoadingAssociations(true);
        const response = await api.get('/api/admin/associations');
        setAssociations(response.data.filter(assoc => assoc.is_active));
      } catch (error) {
        console.error('Error fetching associations:', error);
        toast.error('Failed to load associations');
      } finally {
        setLoadingAssociations(false);
      }
    };

    fetchAssociations();
  }, [isSuperAdmin]);
  
  // Fetch subadmins for viewer dropdown when needed
  useEffect(() => {
    const fetchSubadmins = async () => {
      // Only fetch subadmins if creating/editing a viewer user
      // and current user is a superadmin or association admin
      if (formik.values.role !== 'viewer' || 
          (!isSuperAdmin && !(currentUser?.is_association_admin && currentUser?.role === 'subadmin'))) {
        return;
      }

      try {
        setLoadingSubadmins(true);
        // For regular subadmins, they can only create viewers linked to themselves
        if (currentUser?.role === 'subadmin' && !currentUser?.is_association_admin) {
          setSubadmins([]);
          return;
        }
        
        // For superadmins and association admins, fetch all available subadmins
        const params = {
          role: 'subadmin'
        };
        if (currentUser?.association_id && !isSuperAdmin) {
          params.association_id = currentUser.association_id;
        }
        
        const response = await api.get('/api/admin/users', { params });
        // Filter to only include active subadmins
        const availableSubadmins = response.data.filter(user => 
          user.role === 'subadmin' && user.is_active
        );
        setSubadmins(availableSubadmins);
      } catch (error) {
        console.error('Error fetching subadmins:', error);
        toast.error('Failed to load subadmins');
      } finally {
        setLoadingSubadmins(false);
      }
    };

    fetchSubadmins();
  }, [formik.values.role, isSuperAdmin, currentUser]);

  // Fetch user data if editing existing one
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setInitialLoading(true);
        
        // For subadmins editing viewers, there are two possible approaches
        if (isViewerMode && currentUser?.role === 'subadmin' && !isSuperAdmin) {
          try {
            // First try the direct API call
            const response = await api.get(`/api/admin/users/${id}`);
            formik.setValues({
              ...response.data,
              password: '' // Don't prefill password
            });
          } catch (directError) {
            // If direct API fails, try to get viewer from the list API
            console.log("Falling back to viewers list to get data");
            const viewersResponse = await api.get('/api/admin/users', { 
              params: { 
                role: 'viewer',
                created_by_id: currentUser.id
              }
            });
            
            // Find the specific viewer we need
            const viewerData = viewersResponse.data.find(user => user.id.toString() === id);
            
            if (viewerData) {
              formik.setValues({
                ...viewerData,
                created_by_id: currentUser.id, // Ensure this is set correctly
                password: '' // Don't prefill password
              });
            } else {
              throw new Error("Viewer not found in results");
            }
          }
        } else {
          // Regular API call for other cases
          const response = await api.get(`/api/admin/users/${id}`);
          formik.setValues({
            ...response.data,
            password: '' // Don't prefill password
          });
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setError('Failed to load user data');
        toast.error('Failed to load user data');
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchUser();
    }
  }, [id, currentUser, isViewerMode, isSuperAdmin]);

  const handleBack = () => {
    if (isViewerMode) {
      navigate('/viewers');
    } else {
      navigate('/admin/users');
    }
  };

  // Disable association admin toggle if another user already has this role
  const [disableAssociationAdmin, setDisableAssociationAdmin] = useState(false);

  useEffect(() => {
    // If Super Admin is creating a subadmin, always set is_association_admin to true
    if (isSuperAdmin && formik.values.role === 'subadmin') {
      formik.setFieldValue('is_association_admin', true);
    }
    
    // If ANY subadmin is creating a viewer, automatically set created_by_id to the current user
    if (currentUser?.role === 'subadmin' && formik.values.role === 'viewer') {
      formik.setFieldValue('created_by_id', currentUser.id);
    }
  }, [isSuperAdmin, formik.values.role, currentUser]);

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">
          {id 
            ? (isViewerMode ? 'Edit Viewer' : 'Edit User') 
            : (isViewerMode ? 'Create Viewer' : 'Create User')
          }
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                disabled={loading || (!!id && !isSuperAdmin)} // Only superadmin can change username of existing users
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="full_name"
                name="full_name"
                label="Full Name"
                value={formik.values.full_name || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                helperText={formik.touched.full_name && formik.errors.full_name}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label="Phone"
                value={formik.values.phone || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                disabled={loading}
              />
            </Grid>

            {/* Hide the role field for Association Admins as they can only add SubAdmins */}
            {!isAssociationAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formik.values.role}
                    onChange={(e) => {
                      formik.handleChange(e);
                      // Reset related fields when role changes
                      if (e.target.value === 'viewer') {
                        formik.setFieldValue('is_association_admin', false);
                      } else if (e.target.value === 'subadmin' && isSuperAdmin) {
                        // Force association_admin to be true for subadmins created by superadmins
                        formik.setFieldValue('is_association_admin', true);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.role && Boolean(formik.errors.role)}
                    label="Role"
                    disabled={loading || isViewerMode || (!isSuperAdmin && formik.values.role === 'superadmin')}
                    required
                  >
                    {!isViewerMode && <MenuItem value="subadmin">Sub Admin</MenuItem>}
                    {(!isSuperAdmin || isViewerMode) && <MenuItem value="viewer">Viewer</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* For Association Admins, show a display-only field indicating they can only add SubAdmins */}
            {isAssociationAdmin && !id && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formik.values.role}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (e.target.value === 'viewer') {
                        formik.setFieldValue('is_association_admin', false);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.role && Boolean(formik.errors.role)}
                    label="Role"
                    required
                  >
                    <MenuItem value="subadmin">Sub Admin</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* For Viewers, show a dropdown to select which SubAdmin they belong to */}
            {formik.values.role === 'subadmin' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="distributor_name"
                  name="distributor_name"
                  label="Distributor Name"
                  value={formik.values.distributor_name || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.distributor_name && Boolean(formik.errors.distributor_name)}
                  helperText={formik.touched.distributor_name && formik.errors.distributor_name}
                  disabled={loading}
                  required={formik.values.role === 'subadmin'}
                />
              </Grid>
            )}

            {formik.values.role === 'subadmin' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    id="city"
                    name="city"
                    value={formik.values.city || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    label="City"
                    disabled={loading}
                    required={formik.values.role === 'subadmin'}
                  >
                    <MenuItem value="">Select City</MenuItem>
                    <MenuItem value="North">North</MenuItem>
                    <MenuItem value="South">South</MenuItem>
                    <MenuItem value="Saurashtra">Saurashtra</MenuItem>
                    <MenuItem value="Central">Central</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formik.values.role === 'viewer' && (
              <Grid item xs={12} md={6}>
                {currentUser?.role === 'subadmin' ? (
                  // For ANY subadmin, just show a read-only field with their own name
                  <TextField
                    fullWidth
                    label="Linked to SubAdmin"
                    value={(currentUser?.distributor_name || currentUser?.full_name || currentUser?.username) + " (You)"}
                    InputProps={{ readOnly: true }}
                    helperText="Viewers are automatically linked to you"
                  />
                ) : (
                  // For superadmins only, show the dropdown
                  <FormControl fullWidth>
                    <InputLabel id="created-by-label">Linked to SubAdmin</InputLabel>
                    <Select
                      labelId="created-by-label"
                      id="created_by_id"
                      name="created_by_id"
                      value={formik.values.created_by_id || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.created_by_id && Boolean(formik.errors.created_by_id)}
                      label="Linked to SubAdmin"
                      required={formik.values.role === 'viewer'}
                      disabled={loading}
                    >
                      <MenuItem value="">Select a SubAdmin</MenuItem>
                      {subadmins?.map(subadmin => (
                        <MenuItem key={subadmin.id} value={subadmin.id}>
                          {subadmin.distributor_name || subadmin.full_name || subadmin.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
            )}

            {isSuperAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loading || loadingAssociations || formik.values.role === 'superadmin'}>
                  <InputLabel id="association-label">Association</InputLabel>
                  <Select
                    labelId="association-label"
                    id="association_id"
                    name="association_id"
                    value={formik.values.association_id || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.association_id && Boolean(formik.errors.association_id)}
                    label="Association"
                    required={formik.values.role !== 'superadmin'}
                  >
                    <MenuItem value="">None</MenuItem>
                    {associations.map(assoc => (
                      <MenuItem key={assoc.id} value={assoc.id}>{assoc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label={id ? "New Password (leave blank to keep current)" : "Password"}
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                disabled={loading}
                required={!id}
              />
            </Grid>

            {formik.values.role === 'subadmin' && isSuperAdmin && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={true}
                      name="is_association_admin"
                      color="primary"
                      disabled={true}
                    />
                  }
                  label="Association Admin"
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 2 }}>
                  All Sub-Admins created by Super Admins are Association Admins
                </Typography>
              </Grid>
            )}
            
            {formik.values.role === 'subadmin' && !isSuperAdmin && currentUser?.is_association_admin && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={false}
                      name="is_association_admin"
                      color="primary"
                      disabled={true}
                    />
                  }
                  label="Association Admin"
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 2 }}>
                  Only Super Admins can create Association Admins
                </Typography>
              </Grid>
            )}

            {id && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_active}
                      onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                      name="is_active"
                      color="primary"
                      disabled={loading || currentUser?.id === parseInt(id)} // Can't deactivate yourself
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default UserForm;